import React, { useMemo, useState } from "react";
import { useDebounce } from "@src/hooks/useDebounce";
import { useQueryData } from "@src/hooks/useQueryData";
import { Input } from "@shared/ui/input";
import { PickerList } from "@shared/ui/picker-list";
import { PickerSurface } from "@shared/ui/picker-surface";
import type { BusinessEntityPickerConfig } from "../types";
import "./BusinessEntityPicker.scss";

type IdValue = string | number;

/** Пропсы {@link BusinessEntityPicker}. */
export interface BusinessEntityPickerProps<T extends object, Id extends IdValue = number> {
    config: BusinessEntityPickerConfig<T, Id>;
    value: Id[];
    onChange: (ids: Id[]) => void;
    selectionMode?: "single" | "multiple";
    disabled?: boolean;
    /** Нижняя область picker surface: закрытие fullscreen, confirm/cancel или доменное действие. */
    footerSlot?: React.ReactNode;
}

/**
 * Поиск + touch-list для fullscreen picker (BusinessPickerField window, overlay editors).
 */
export function BusinessEntityPicker<T extends object, Id extends IdValue = number>({
    config,
    value,
    onChange,
    selectionMode = "single",
    disabled = false,
    footerSlot,
}: BusinessEntityPickerProps<T, Id>) {
    const [searchQuery, setSearchQuery] = useState("");
    const debouncedSearchQuery = useDebounce(searchQuery, config.debounceDelay ?? 500);
    const minSearchLength = config.minSearchLength ?? 2;
    const searchEnabled = !disabled && debouncedSearchQuery.length >= minSearchLength;

    const searchQueryResult = useQueryData({
        queryKey: [...config.queryKeyBase, "picker", debouncedSearchQuery],
        queryFn: async ({ signal }) => {
            if (debouncedSearchQuery.length < minSearchLength) return [];
            return config.loadOptions(debouncedSearchQuery, signal);
        },
        enabled: searchEnabled,
    });

    const options = useMemo(() => {
        if (!searchEnabled) return null;
        const raw = searchQueryResult.data ?? [];
        return config.transformOptions != null ? config.transformOptions(raw) : raw;
    }, [searchEnabled, searchQueryResult.data, config]);

    const selectedIdSet = useMemo(() => new Set(value), [value]);

    const handleSelect = (entity: T) => {
        const id = config.getId(entity);
        if (selectionMode === "single") {
            onChange([id]);
            return;
        }
        const next = new Set(selectedIdSet);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        onChange([...next]);
    };

    return (
        <PickerSurface className="business_entity_picker">
            <PickerSurface.Controls>
                <Input
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Поиск"
                    disabled={disabled}
                />
            </PickerSurface.Controls>
            <PickerSurface.Body>
                <PickerList
                    items={options}
                    isLoading={searchQueryResult.isLoading === true}
                    isRefreshing={searchQueryResult.isFetching === true && searchQueryResult.isLoading === false}
                    isSelected={(item) => selectedIdSet.has(config.getId(item))}
                    renderItem={(item) => config.getOptionLabel(item)}
                    onSelect={handleSelect}
                    idleText={config.idleText ?? `Введите минимум ${minSearchLength} символа`}
                    emptyText={config.emptyText ?? "Нет результатов"}
                />
            </PickerSurface.Body>
            {footerSlot != null && <PickerSurface.Footer>{footerSlot}</PickerSurface.Footer>}
        </PickerSurface>
    );
}
